import { FormEvent, useEffect, useMemo, useState } from "react";
import { Loader2, Plus, RefreshCw, Image as ImageIcon, X } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import MediaPicker from "@/components/admin/MediaPicker";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";

interface EventRow {
  id: string;
  title: string;
  slug: string;
  event_date: string;
  start_time: string | null;
  end_time: string | null;
  date_time_label: string | null;
  time_range: string | null;
  location_name: string | null;
  location_line_1: string | null;
  location_line_2: string | null;
  format: string | null;
  category: string | null;
  description: string | null;
  registration_url: string | null;
  image_url: string | null;
  is_featured: boolean;
  is_published: boolean;
  show_in_announcement_bar: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

type PubFilter = "all" | "published" | "draft";
type TimeFilter = "all" | "upcoming" | "past";

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);

const emptyDraft = (): Partial<EventRow> => ({
  title: "",
  slug: "",
  event_date: new Date().toISOString().slice(0, 10),
  start_time: "",
  end_time: "",
  date_time_label: "",
  time_range: "",
  location_name: "",
  location_line_1: "",
  location_line_2: "",
  format: "",
  category: "",
  description: "",
  registration_url: "",
  image_url: "",
  is_featured: false,
  is_published: false,
  show_in_announcement_bar: false,
  sort_order: 0,
});

const isUrl = (v: string) => {
  try {
    new URL(v);
    return true;
  } catch {
    return false;
  }
};

const todayStart = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const AdminEvents = () => {
  const { user } = useAdminAuth();
  const [rows, setRows] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pubFilter, setPubFilter] = useState<PubFilter>("all");
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("all");
  const [editing, setEditing] = useState<Partial<EventRow> | null>(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    document.title = "Events | Hadassah Homes Admin";
    void load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("events")
      .select("*")
      .order("event_date", { ascending: false });
    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }
    setRows((data ?? []) as EventRow[]);
    setLoading(false);
  };

  const filtered = useMemo(() => {
    const today = todayStart();
    return rows.filter((r) => {
      if (pubFilter === "published" && !r.is_published) return false;
      if (pubFilter === "draft" && r.is_published) return false;
      const d = new Date(r.event_date);
      if (timeFilter === "upcoming" && d < today) return false;
      if (timeFilter === "past" && d >= today) return false;
      return true;
    });
  }, [rows, pubFilter, timeFilter]);

  const openNew = () => {
    setSlugTouched(false);
    setEditing(emptyDraft());
  };
  const openEdit = (row: EventRow) => {
    setSlugTouched(true);
    setEditing(row);
  };

  const togglePublish = async (row: EventRow) => {
    const { data, error: err } = await supabase
      .from("events")
      .update({ is_published: !row.is_published, updated_by: user?.id ?? null })
      .eq("id", row.id)
      .select()
      .single();
    if (err) return toast.error(err.message);
    setRows((prev) => prev.map((r) => (r.id === row.id ? (data as EventRow) : r)));
    toast.success(row.is_published ? "Unpublished" : "Published");
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    const title = (editing.title ?? "").trim();
    const slug = (editing.slug ?? "").trim() || slugify(title);
    if (!title) return toast.error("Title is required");
    if (!slug) return toast.error("Slug is required");
    if (!editing.event_date) return toast.error("Event date is required");
    if (editing.registration_url && !isUrl(editing.registration_url)) {
      return toast.error("Registration URL must be a valid URL");
    }

    setSaving(true);
    const payload: Record<string, unknown> = {
      title,
      slug,
      event_date: editing.event_date,
      start_time: editing.start_time || null,
      end_time: editing.end_time || null,
      date_time_label: editing.date_time_label?.trim() || null,
      time_range: editing.time_range?.trim() || null,
      location_name: editing.location_name?.trim() || null,
      location_line_1: editing.location_line_1?.trim() || null,
      location_line_2: editing.location_line_2?.trim() || null,
      format: editing.format?.trim() || null,
      category: editing.category?.trim() || null,
      description: editing.description?.trim() || null,
      registration_url: editing.registration_url?.trim() || null,
      image_url: editing.image_url?.trim() || null,
      is_featured: !!editing.is_featured,
      is_published: !!editing.is_published,
      show_in_announcement_bar: !!editing.show_in_announcement_bar,
      sort_order: Number(editing.sort_order ?? 0) || 0,
      updated_by: user?.id ?? null,
    };

    if (editing.id) {
      const { data, error: err } = await supabase
        .from("events")
        .update(payload as never)
        .eq("id", editing.id)
        .select()
        .single();
      setSaving(false);
      if (err) return toast.error(err.message);
      setRows((prev) => prev.map((r) => (r.id === editing.id ? (data as EventRow) : r)));
      toast.success("Event saved");
    } else {
      const { data, error: err } = await supabase
        .from("events")
        .insert({ ...payload, created_by: user?.id ?? null } as never)
        .select()
        .single();
      setSaving(false);
      if (err) return toast.error(err.message);
      setRows((prev) => [data as EventRow, ...prev]);
      toast.success("Event created");
    }
    setEditing(null);
  };

  const updateField = <K extends keyof EventRow>(key: K, value: EventRow[K]) => {
    setEditing((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  return (
    <AdminLayout
      title="Events"
      description="Manage public event cards and event-related announcement items. Only published events appear on the public Events page."
      publicHref="/events"
      publicLabel="View Events page"
    >
      <div className="mx-auto max-w-7xl space-y-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={pubFilter}
              onChange={(e) => setPubFilter(e.target.value as PubFilter)}
              className="h-10 rounded-none border border-input bg-background px-3 text-sm"
            >
              <option value="all">All statuses</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value as TimeFilter)}
              className="h-10 rounded-none border border-input bg-background px-3 text-sm"
            >
              <option value="all">All dates</option>
              <option value="upcoming">Upcoming</option>
              <option value="past">Past</option>
            </select>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => void load()}
              disabled={loading}
              className="rounded-none"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button variant="gold" onClick={openNew} className="rounded-none">
              <Plus className="mr-2 h-4 w-4" />
              New event
            </Button>
          </div>
        </div>

        <div className="border border-border bg-card">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="p-6 text-destructive">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-sm text-muted-foreground">
                No events match your filters. Create one to display on the public Events page.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Title</th>
                    <th className="px-4 py-3 hidden lg:table-cell">Category</th>
                    <th className="px-4 py-3 hidden md:table-cell">Top bar</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((r) => (
                    <tr key={r.id} className="hover:bg-muted/30">
                      <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                        {new Date(r.event_date).toLocaleDateString()}
                      </td>
                      <td
                        className="cursor-pointer px-4 py-3 font-medium text-foreground"
                        onClick={() => openEdit(r)}
                      >
                        <div className="line-clamp-2">{r.title}</div>
                        <p className="text-[11px] text-muted-foreground">{r.slug}</p>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell">
                        {r.category ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                        {r.show_in_announcement_bar ? "Yes" : "No"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                            r.is_published
                              ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30"
                              : "bg-muted text-muted-foreground border-border"
                          }`}
                        >
                          {r.is_published ? "Published" : "Draft"}
                        </span>
                        {r.is_featured ? (
                          <span className="ml-1 inline-flex items-center border border-accent/30 bg-accent/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-accent">
                            Featured
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => togglePublish(r)}
                            className="rounded-none"
                          >
                            {r.is_published ? "Unpublish" : "Publish"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEdit(r)}
                            className="rounded-none"
                          >
                            Edit
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          {filtered.length} of {rows.length} events
        </p>
      </div>

      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
          {editing ? (
            <form onSubmit={handleSave}>
              <SheetHeader>
                <SheetTitle className="font-headline text-2xl">
                  {editing.id ? "Edit event" : "New event"}
                </SheetTitle>
                <SheetDescription>
                  Published events appear on /events. Draft events are admin-only.
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-5">
                <Field label="Title *">
                  <Input
                    required
                    value={editing.title ?? ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEditing((prev) =>
                        prev
                          ? {
                              ...prev,
                              title: v,
                              slug: slugTouched ? prev.slug : slugify(v),
                            }
                          : prev
                      );
                    }}
                    className="rounded-none"
                  />
                </Field>

                <Field label="Slug *">
                  <Input
                    required
                    value={editing.slug ?? ""}
                    onChange={(e) => {
                      setSlugTouched(true);
                      updateField("slug", slugify(e.target.value));
                    }}
                    className="rounded-none"
                  />
                </Field>

                <div className="grid grid-cols-3 gap-3">
                  <Field label="Date *">
                    <Input
                      type="date"
                      required
                      value={editing.event_date ?? ""}
                      onChange={(e) => updateField("event_date", e.target.value)}
                      className="rounded-none"
                    />
                  </Field>
                  <Field label="Start time">
                    <Input
                      type="time"
                      value={editing.start_time ?? ""}
                      onChange={(e) => updateField("start_time", e.target.value)}
                      className="rounded-none"
                    />
                  </Field>
                  <Field label="End time">
                    <Input
                      type="time"
                      value={editing.end_time ?? ""}
                      onChange={(e) => updateField("end_time", e.target.value)}
                      className="rounded-none"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Display date label">
                    <Input
                      value={editing.date_time_label ?? ""}
                      onChange={(e) => updateField("date_time_label", e.target.value)}
                      placeholder="Saturday, May 9"
                      className="rounded-none"
                    />
                  </Field>
                  <Field label="Display time range">
                    <Input
                      value={editing.time_range ?? ""}
                      onChange={(e) => updateField("time_range", e.target.value)}
                      placeholder="2:00 PM - 4:00 PM"
                      className="rounded-none"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Category">
                    <Input
                      value={editing.category ?? ""}
                      onChange={(e) => updateField("category", e.target.value)}
                      placeholder="For Buyers & Investors"
                      className="rounded-none"
                    />
                  </Field>
                  <Field label="Format">
                    <Input
                      value={editing.format ?? ""}
                      onChange={(e) => updateField("format", e.target.value)}
                      placeholder="Free · In person · 2 hours"
                      className="rounded-none"
                    />
                  </Field>
                </div>

                <Field label="Location name">
                  <Input
                    value={editing.location_name ?? ""}
                    onChange={(e) => updateField("location_name", e.target.value)}
                    className="rounded-none"
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Address line 1">
                    <Input
                      value={editing.location_line_1 ?? ""}
                      onChange={(e) => updateField("location_line_1", e.target.value)}
                      className="rounded-none"
                    />
                  </Field>
                  <Field label="Address line 2">
                    <Input
                      value={editing.location_line_2 ?? ""}
                      onChange={(e) => updateField("location_line_2", e.target.value)}
                      className="rounded-none"
                    />
                  </Field>
                </div>

                <Field label="Description">
                  <Textarea
                    rows={4}
                    value={editing.description ?? ""}
                    onChange={(e) => updateField("description", e.target.value)}
                    className="rounded-none"
                  />
                </Field>

                <Field label="Registration URL">
                  <Input
                    type="url"
                    value={editing.registration_url ?? ""}
                    onChange={(e) => updateField("registration_url", e.target.value)}
                    placeholder="https://eventbrite.com/..."
                    className="rounded-none"
                  />
                </Field>

                <Field label="Event image (optional — fallback used if empty)">
                  <div className="space-y-2">
                    {editing.image_url ? (
                      <div className="flex items-start gap-3">
                        <img
                          src={editing.image_url}
                          alt=""
                          className="h-24 w-32 border border-border object-cover"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display = "none";
                          }}
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => updateField("image_url", "" as never)}
                          className="rounded-none"
                        >
                          <X className="mr-1 h-3 w-3" /> Remove
                        </Button>
                      </div>
                    ) : null}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setPickerOpen(true)}
                      className="rounded-none"
                    >
                      <ImageIcon className="mr-2 h-4 w-4" />
                      Choose from Media Library
                    </Button>
                    <Input
                      value={editing.image_url ?? ""}
                      onChange={(e) => updateField("image_url", e.target.value)}
                      placeholder="Or paste a direct image URL"
                      className="rounded-none"
                    />
                  </div>
                </Field>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Sort order">
                    <Input
                      type="number"
                      value={editing.sort_order ?? 0}
                      onChange={(e) =>
                        updateField("sort_order", (Number(e.target.value) || 0) as never)
                      }
                      className="rounded-none"
                    />
                  </Field>
                  <div className="space-y-2 pt-6 text-sm">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!editing.is_published}
                        onChange={(e) => updateField("is_published", e.target.checked as never)}
                      />
                      Published
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!editing.is_featured}
                        onChange={(e) => updateField("is_featured", e.target.checked as never)}
                      />
                      Featured
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!editing.show_in_announcement_bar}
                        onChange={(e) =>
                          updateField("show_in_announcement_bar", e.target.checked as never)
                        }
                      />
                      Show in top bar
                    </label>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button type="submit" variant="gold" disabled={saving} className="rounded-none">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditing(null)}
                    className="rounded-none"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </form>
          ) : null}
        </SheetContent>
      </Sheet>

      <MediaPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={(asset) => updateField("image_url", asset.public_url)}
      />
    </AdminLayout>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="mb-2 block text-xs uppercase tracking-[0.14em] text-muted-foreground">
      {label}
    </label>
    {children}
  </div>
);

export default AdminEvents;
